import { useEffect, useRef } from 'react';
import { useShiftStore } from './shift-store';
import { addMilliseconds, subMilliseconds } from 'date-fns';
import { setTimeoutAt } from '../time';
import { INACTIVE_TIMEOUT_MS, INACTIVE_PROMPT_ADVANCE_MS } from './util';
import { useCurrentShift, useShiftControls } from './shift-controller';

/**
 * Registers timoeouts to update shift state activity.
 *
 * Should be scoped globally.
 */
export const ShiftTimeoutListeners = () => {
  const lastActiveAt = useShiftStore((s) => s.lastActiveAt);
  const status = useShiftStore((s) => s.status);
  const inactiveTimeout = useRef<NodeJS.Timeout>();
  const idleTimeout = useRef<NodeJS.Timeout>();

  // Continually set timeouts for future when
  useEffect(() => {
    const now = new Date();
    const idleAt = addMilliseconds(lastActiveAt, INACTIVE_TIMEOUT_MS);
    const inactiveAt = subMilliseconds(idleAt, INACTIVE_PROMPT_ADVANCE_MS);

    console.debug(`User shift timeouts`, {
      lastActive: lastActiveAt.toISOString(),
      inactiveAt: inactiveAt.toISOString(),
      idleAt: idleAt.toISOString(),
    });

    if (status !== 'inactive' && inactiveAt > now) {
      inactiveTimeout.current = setTimeoutAt(() => {
        console.debug('[Inactive timeout] Setting shift to inactive');
        useShiftStore.setState({ status: 'inactive' });
      }, inactiveAt);
    }

    if (status !== 'idle' && idleAt > now) {
      idleTimeout.current = setTimeoutAt(() => {
        console.debug('[Inactive timeout] Setting shift to idle');
        useShiftStore.setState({ status: 'idle' });
      }, idleAt);
    }

    return () => {
      if (inactiveTimeout.current) clearTimeout(inactiveTimeout.current);
      if (idleTimeout.current) clearTimeout(idleTimeout.current);
    };
  }, [lastActiveAt, status]);

  return null;
};

/**
 * Ends the shift on the backend when it's idle.
 */
export const IdleShiftTerminator = () => {
  const shift = useCurrentShift()
  const hasShift = shift.shift != null
  const controls = useShiftControls();
  const status = useShiftStore((s) => s.status);

  useEffect(() => {
    if (status === 'idle' && hasShift) {
      if (hasShift) {
        useShiftStore.setState({ hasBreached: true });
        controls.endShift();
      }
    }
  }, [status, hasShift]);

  return null;
};
